import React from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Button,
  Stack,
  InputAdornment,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import PageTitle from '../../components/UI/PageTitle';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import apiClient from '../../api/client';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useAuth } from '../../contexts/AuthContext';

interface TerminalSettingsResponse {
  organization: {
    id: string;
    name: string;
    code?: string;
  };
  apiKey: string;
  createdAt: string;
  updatedAt: string;
  lastUsedAt: string | null;
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return 'Н/Д';
  }
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const SettingsPage: React.FC = () => {
  const { selectedOrgId } = useOrganization();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showApiKey, setShowApiKey] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const isSuperAdmin = user?.role === 'super_admin';
  const organizationIdForRequest = isSuperAdmin ? selectedOrgId : user?.organizationId;

  const { data, isLoading, isError, error } = useQuery<TerminalSettingsResponse>(
    ['terminal-settings', organizationIdForRequest],
    async () => {
      if (isSuperAdmin && !organizationIdForRequest) {
        throw new Error('Organization ID is required for super admin');
      }
      
      // Для супер-админа передаем organizationId, для обычного админа - нет
      const url = isSuperAdmin 
        ? `/api/admin/settings/terminal?organizationId=${organizationIdForRequest}`
        : '/api/admin/settings/terminal';
      
      const response = await apiClient.get(url);
      
      // API возвращает { success: true, data: { ... } }
      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Неверный формат ответа API');
    },
    {
      enabled: isSuperAdmin ? !!organizationIdForRequest : !!user?.organizationId,
      retry: false
    }
  );



  const regenerateMutation = useMutation(
    async () => {
      if (isSuperAdmin && !organizationIdForRequest) {
        throw new Error('Organization ID is required for super admin');
      }
      
      // Для супер-админа передаем organizationId, для обычного админа - нет
      const url = isSuperAdmin 
        ? `/api/admin/settings/terminal/regenerate?organizationId=${organizationIdForRequest}`
        : '/api/admin/settings/terminal/regenerate';
      
      const response = await apiClient.post(url);
      
      // API возвращает { success: true, data: { ... } }
      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Неверный формат ответа API');
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['terminal-settings', organizationIdForRequest]);
        setSnackbar({
          open: true,
          message: 'API ключ успешно перегенерирован',
          severity: 'success'
        });
      },
      onError: (error: any) => {
        setSnackbar({
          open: true,
          message: error?.response?.data?.message || 'Ошибка при перегенерации ключа',
          severity: 'error'
        });
      }
    }
  );

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({
        open: true,
        message: `${label} скопирован в буфер обмена`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Ошибка при копировании',
        severity: 'error'
      });
    }
  };

  const handleRegenerate = () => {
    if (!data?.apiKey) {
      setSnackbar({
        open: true,
        message: 'Отсутствует действующий ключ организации ключа',
        severity: 'error'
      });
      return;
    }
    const confirmed = window.confirm('Перегенерировать API ключ? Старый ключ перестанет работать.');
    if (!confirmed) {
      return;
    }
    regenerateMutation.mutate();
  };

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  return (
    <>
      <Helmet>
        <title>Настройки — ELEKTROM</title>
      </Helmet>

      <PageTitle title="Настройки терминала" />



      {isSuperAdmin && !organizationIdForRequest ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1">
            Выберите организацию в верхнем меню, чтобы получить доступ к настройкам.
          </Typography>
        </Paper>
      ) : isLoading ? (
        <LoadingSpinner />
      ) : isError ? (
        <Paper sx={{ p: 3 }}>
          <Alert severity="error">
            {(error as any)?.response?.data?.message || 'Не удалось загрузить настройки'}
          </Alert>
        </Paper>
      ) : data && data.organization && data.apiKey ? (
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">Данные терминала</Typography>
                <Typography variant="body2" color="text.secondary">
                  Используйте эти данные для настройки приложения self-service.
                </Typography>
              </Box>

              <TextField
                label="ID организации"
                value={data.organization.id}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Скопировать">
                        <IconButton
                          aria-label="Скопировать ID организации"
                          onClick={() => handleCopy(data.organization.id, 'ID организации')}
                          size="small"
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                label="API ключ терминала"
                value={data.apiKey || ''}
                type={showApiKey ? 'text' : 'password'}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showApiKey ? 'Скрыть ключ' : 'Показать ключ'}>
                        <IconButton
                          aria-label={showApiKey ? 'Скрыть API ключ' : 'Показать API ключ'}
                          onClick={() => setShowApiKey((prev) => !prev)}
                          size="small"
                        >
                          {showApiKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Скопировать">
                        <IconButton
                          aria-label="Скопировать API ключ"
                          onClick={() => handleCopy(data.apiKey || '', 'API ключ')}
                          size="small"
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={handleRegenerate}
                  disabled={regenerateMutation.isLoading}
                >
                  Перегенерировать API ключ
                </Button>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Создан: {formatDate(data.createdAt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Обновлен: {formatDate(data.updatedAt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Последнее использование: {formatDate(data.lastUsedAt)}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1">Нет данных для организации.</Typography>
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SettingsPage;
